import { getFullName } from '@provision/formatting-utils';
import { parseSignalNotificationLog } from '@provision/signal-notification-log';
import type { ConversationUrgencyRaw, SignalIncidentRaw } from 'src/types';
import type {
  SignalIncident,
  SignalIncidentDisplayState,
  SignalIncidentUrgency,
  SignalIncidentUrgencyTone,
} from './signal-incident.types';

const URGENCY_TONE_MAP = new Map<string, SignalIncidentUrgencyTone>([
  ['life_safety', 'life-safety'],
  ['urgent', 'urgent'],
  ['low', 'low'],
  ['normal', 'normal'],
]);

export function mapSignalIncidents(
  signalIncidents?: SignalIncidentRaw[] | null,
): SignalIncident[] {
  return (signalIncidents ?? []).map((signalIncident) => ({
    ...signalIncident,
    displayState: mapSignalIncidentDisplayState(signalIncident.status),
    resolverDisplayName: getFullName(signalIncident.resolver).trim() || null,
    urgency: mapSignalIncidentUrgency(signalIncident.conversationUrgency),
    notificationLog: parseSignalNotificationLog(signalIncident.notificationLog),
  }));
}

function mapSignalIncidentDisplayState(status?: string | null): SignalIncidentDisplayState {
  // Product requirement: canceled and ignored incidents stay actionable in the timeline
  // and are rendered as unresolved until an agent explicitly resolves them from the UI.
  return status === 'resolved' ? 'resolved' : 'unresolved';
}

function mapSignalIncidentUrgency(
  conversationUrgency?: ConversationUrgencyRaw | null,
): SignalIncidentUrgency | null {
  const normalizedId = conversationUrgency?.id?.trim().toLowerCase();
  if (!normalizedId) {
    return null;
  }

  return {
    label: conversationUrgency?.urgencyName ?? normalizedId,
    tone: mapSignalIncidentUrgencyTone(normalizedId),
  };
}

function mapSignalIncidentUrgencyTone(
  conversationUrgencyId?: string | null,
): SignalIncidentUrgencyTone {
  if (conversationUrgencyId && URGENCY_TONE_MAP.has(conversationUrgencyId)) {
    return URGENCY_TONE_MAP.get(conversationUrgencyId) ?? 'neutral';
  }

  return 'neutral';
}
